import _ from 'underscore';

import DataConnector from 'connect/DataConnector';
import {DataTable, DataTableErrorsByRecordId} from 'notebook/data-tables/DataTableConstants';
import {DataTableEditableProps, RowStateManagerProps} from 'notebook/data-tables/DataTableEditable';
import * as DataTableUtil from 'notebook/data-tables/DataTableUtil';
import {ReviewSummary} from 'notebook/reviews/ReviewConstants';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import {BenchlingState} from 'redux-stores/reduceApp';
import {AssayResult, DATA_MISSING} from 'results/assays/AssayRecordConstants';
import getOrFetchResultValidationInConnector, {
  TableAssayValidationEnhancedProps,
} from 'results/validation/getOrFetchResultValidationInConnector';
// @ts-expect-error
import EntryStore from 'stores/EntryStore';
import memoizeLastCall from 'util/memoizeLastCall';
import pick from 'util/pick';
import preserveShallowEquality from 'util/preserveShallowEquality';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import StageEntryStore from 'workflows/stage-entries/StageEntryStore';
import {RegistrationRecordList, StageRun, StageRunSample} from 'workflows/WorkflowConstants';

type AssayResultsById = BenchlingState['models']['results']['assayResultsById'];

function _filterResults(assayResultsById: AssayResultsById, ids: Array<string>): Array<AssayResult> {
  // eslint-disable-next-line no-restricted-syntax
  const records = _.values(pick(assayResultsById, ...ids));
  if (records.some((record) => record === DATA_MISSING)) {
    return [];
  }
  return records as Array<AssayResult>;
}

function _rowIdsFromTable(dataTable: DataTable): Array<string> {
  return dataTable.rows.map((row) => row.euid);
}

function _assayResultsFromTable(
  dataTable: DataTable,
  assayResultsById: AssayResultsById
): Array<AssayResult> {
  if (!DataTableUtil.isAssayTypeResult(dataTable.config)) {
    return [];
  }

  const resultIds = _rowIdsFromTable(dataTable);
  return _filterResults(assayResultsById, resultIds);
}

export interface DataTableEditableEnhancedProps extends TableAssayValidationEnhancedProps {
  resultsInTable: Array<AssayResult>;
  dataTableErrorsByRecordId: DataTableErrorsByRecordId;
  registrationRecordListsById: {[id: string]: RegistrationRecordList};
  review?: ReviewSummary;
  stageRuns: Array<StageRun>;
  stageRunSampleById: {[id: string]: StageRunSample};
}

/**
 * @deprecated This table is no longer supported, please do not further proliferate its use.
 */
export default class DataTableEditableConnector extends DataConnector<
  DataTableEditableProps & RowStateManagerProps,
  DataTableEditableEnhancedProps
> {
  memoizedEnhanceProps: (
    workflowState: BenchlingState['models']['workflows'],
    registrationRecordListsById: BenchlingState['models']['registrationRecordListsById']
  ) => {
    registrationRecordListsById: DataTableEditableEnhancedProps['registrationRecordListsById'];
    stageRuns: DataTableEditableEnhancedProps['stageRuns'];
    stageRunSampleById: DataTableEditableEnhancedProps['stageRunSampleById'];
  };
  memoizedEnhancePropsForResultsInTable: (
    dataTable: DataTable,
    assayResultsById: AssayResultsById,
    dataTableErrorsByRecordId: DataTableErrorsByRecordId
  ) => {
    resultsInTable: DataTableEditableEnhancedProps['resultsInTable'];
    dataTableErrorsByRecordId: DataTableErrorsByRecordId;
  };
  preservedEqualityGetValidation: (resultIds: Array<string>) => TableAssayValidationEnhancedProps;

  // Listening to these stores causes DataTableEditable to re-render when typing into an entry. This can cause
  // lag if there are expensive re-render functions. We avoid this by ensuring all mapped
  // props are PureComponent-friendly, in particular by using memoizeLastCall.
  override stores = [EntryStore, StageEntryStore];

  constructor(props: DataTableEditableProps & RowStateManagerProps) {
    super(props);
    this.memoizedEnhanceProps = memoizeLastCall((workflowState, registrationRecordListsById) => {
      const workflowSelector = new WorkflowSelector(workflowState);
      return {
        registrationRecordListsById,
        stageRuns: workflowSelector.getStageRunsForEntry(this.props.fileId),
        stageRunSampleById: workflowSelector.getStageRunSampleById(),
      };
    });
    this.memoizedEnhancePropsForResultsInTable = memoizeLastCall(
      (dataTable, assayResultsById, dataTableErrorsByRecordId) => {
        const resultsInTable: Array<AssayResult> = _assayResultsFromTable(dataTable, assayResultsById);
        return {
          resultsInTable,
          dataTableErrorsByRecordId: pick(dataTableErrorsByRecordId, ..._rowIdsFromTable(dataTable)),
        };
      }
    );
    this.preservedEqualityGetValidation = preserveShallowEquality((resultIds: Array<string>) =>
      getOrFetchResultValidationInConnector(this, resultIds)
    );
  }

  enhanceProps(): DataTableEditableEnhancedProps {
    const state = this.reduxState();
    return {
      ...this.memoizedEnhanceProps(state.models.workflows, state.models.registrationRecordListsById),
      ...this.memoizedEnhancePropsForResultsInTable(
        this.props.dataTable,
        state.models.results.assayResultsById,
        state.ui.dataTables.dataTableErrorsByRecordId
      ),
      ...this.preservedEqualityGetValidation(
        _assayResultsFromTable(this.props.dataTable, state.models.results.assayResultsById).map(
          (result) => result.id
        )
      ),
      review: ReviewUtil.getReviewFromStore(this.props.fileId) ?? undefined,
    };
  }
}
